import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { TranslateModule } from '@ngx-translate/core';
import { Company } from '../company.model';
import {
  COMPANY_REGISTRATION_ATTACHMENT_TYPES,
  CompanyRegistrationAttachment,
  CompanyRegistrationAttachmentCategory,
  CompanyService
} from '../company.service';
import { Customer } from '../../customer/customer.model';
import { Role } from '../../customer/customer-role.model';
import { CustomerService } from '../../customer/customer.service';
import { SessionStorageService } from '../../helpers/session-storage.service';
import { TransportationAgency } from '../../models/transportation-agency.model';
import { FreightForwarder } from '../../models/freight-forwarder.model';
import { TransportationAgencyService } from '../../services/transportation-agency.service';
import { FreightForwarderService } from '../../services/freight-forwarder.service';
import { StorageService } from '../../services/storage.service';

@Component({
  selector: 'app-company-view',
  standalone: true,
  templateUrl: './company-view.component.html',
  styleUrls: ['../../shared/management-pages.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    AvatarModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ToastModule,
    TooltipModule,
    TranslateModule
  ],
  providers: [MessageService]
})
export class CompanyViewComponent {
  private readonly companyService = inject(CompanyService);
  private readonly customerService = inject(CustomerService);
  private readonly transportationAgencyService = inject(TransportationAgencyService);
  private readonly freightForwarderService = inject(FreightForwarderService);
  private readonly storageService = inject(StorageService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly messageService = inject(MessageService);
  private readonly fb = inject(FormBuilder);

  company = signal<Company | null>(null);
  transportationAgency = signal<TransportationAgency | null>(null);
  freightForwarder = signal<FreightForwarder | null>(null);
  employees = signal<Customer[]>([]);
  roles = signal<Role[]>([]);
  isLoading = signal(false);
  isSaving = signal(false);
  isRegisteringAgency = signal(false);
  isRegisteringForwarder = signal(false);
  error = signal<string | null>(null);
  inviteDialogVisible = signal(false);
  transportationAgencyDialogVisible = signal(false);
  roleDialogVisible = signal(false);
  roleCustomer = signal<Customer | null>(null);
  inviteRoleIds = signal<Set<number>>(new Set<number>());
  selectedRoleIds = signal<Set<number>>(new Set<number>());
  companyFiles = signal<CompanyRegistrationAttachment[]>([]);
  fileDialogVisible = signal(false);
  isUploadingFile = signal(false);
  selectedFileType = signal<CompanyRegistrationAttachmentCategory>('VAT_CERTIFICATE');
  selectedFile = signal<File | null>(null);
  readonly companyFileTypes = COMPANY_REGISTRATION_ATTACHMENT_TYPES;

  inviteForm = this.fb.group({
    name: ['', Validators.required],
    emailAddress: ['', [Validators.required, Validators.email]],
    phoneNumber: ['', Validators.required],
    corporateRole: ['EMPLOYEE', Validators.required],
    address: ['']
  });

  transportationAgencyForm = this.fb.group({
    motAgencyLicenseNumber: ['']
  });

  ngOnInit(): void {
    this.loadCompanyContext();
  }

  loadCompanyContext(): void {
    const sessionCompany = this.sessionStorage.getCompany();
    if (!sessionCompany?.id) {
      this.error.set('No company context found. Please log in again.');
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    this.companyService.getCompanyById(sessionCompany.id).pipe(
      switchMap(response => {
        const company = response.data;
        this.company.set(company);
        return forkJoin({
          employees: this.customerService.getCustomersByCompany(company.id),
          roles: this.customerService.getAllRoles(),
          companyFiles: this.companyService.getCompanyRegistrationAttachments(company.id),
          transportationAgency: company.transportationAgencyId
            ? this.transportationAgencyService.findTransportationAgencyByFilter(undefined, undefined, company.transportationAgencyId, undefined, undefined).pipe(
              catchError(error => {
                console.warn('Failed to load transportation agency:', error);
                return of(null);
              })
            )
            : of(null),
          freightForwarder: company.freightForwarderId
            ? this.freightForwarderService.getFreightForwarder(undefined, company.freightForwarderId, undefined, undefined, undefined).pipe(
              catchError(error => {
                console.warn('Failed to load freight forwarder:', error);
                return of(null);
              })
            )
            : of(null)
        });
      }),
      finalize(() => this.isLoading.set(false)),
      catchError(error => {
        this.error.set(error?.message || 'Unable to load company.');
        return of({ employees: [], roles: [], companyFiles: [], transportationAgency: null, freightForwarder: null });
      })
    ).subscribe(({ employees, roles, companyFiles, transportationAgency, freightForwarder }) => {
      this.employees.set(employees ?? []);
      this.roles.set(roles ?? []);
      this.companyFiles.set(companyFiles ?? []);
      this.transportationAgency.set(transportationAgency?.data ?? null);
      this.freightForwarder.set(freightForwarder ?? null);
    });
  }

  openTransportationAgencyDialog(): void {
    this.transportationAgencyForm.reset({ motAgencyLicenseNumber: '' });
    this.transportationAgencyDialogVisible.set(true);
  }

  registerTransportationAgency(): void {
    const currentCompany = this.company();
    if (!currentCompany?.id) {
      return;
    }

    const agency = new TransportationAgency();
    agency.agencyName = currentCompany.name;
    agency.crNumber = currentCompany.crNumber;
    agency.motAgencyLicenseNumber = this.blankToNull(this.transportationAgencyForm.getRawValue().motAgencyLicenseNumber);
    agency.phoneNumber = this.blankToNull(currentCompany.phoneNumber);
    agency.website = this.blankToNull(currentCompany.websiteURL);
    agency.emailAddress = this.blankToNull(currentCompany.emailAddress);
    agency.address = currentCompany.address;
    agency.logo = currentCompany.logo;
    agency.countryId = currentCompany.countryId;
    agency.location = currentCompany.location ?? null;
    agency.defaultContactId = this.resolveDefaultContactId(currentCompany);
    agency.companyId = currentCompany.id;
    agency.placeId = currentCompany.placeId;

    this.isRegisteringAgency.set(true);
    this.transportationAgencyService.addTransportationAgency(agency).pipe(
      switchMap(response => {
        if (!response.success || !response.data?.rowId) {
          throw new Error(response.message || 'Transportation agency registration failed.');
        }
        const updatedCompany = { ...currentCompany, transportationAgencyId: response.data.rowId };
        this.transportationAgency.set(response.data);
        this.sessionStorage.saveTransportationAgency(response.data);
        return this.companyService.updateCompany(updatedCompany);
      }),
      finalize(() => this.isRegisteringAgency.set(false))
    ).subscribe({
      next: response => {
        if (!response.success) {
          this.messageService.add({ severity: 'error', summary: 'Company update failed', detail: response.message });
          return;
        }
        this.company.set(response.data);
        this.sessionStorage.saveCompany(response.data);
        this.transportationAgencyDialogVisible.set(false);
        this.messageService.add({ severity: 'success', summary: 'Transportation agency registered', detail: 'Company is now registered as a transportation agency.' });
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Registration failed',
        detail: error?.message || 'Unable to register transportation agency.'
      })
    });
  }

  registerFreightForwarder(): void {
    const currentCompany = this.company();
    if (!currentCompany?.id) {
      return;
    }

    const ff = new FreightForwarder();
    ff.name = currentCompany.name;
    ff.address = currentCompany.address;
    ff.countryId = currentCompany.countryId;
    ff.phoneNumber = this.blankToNull(currentCompany.phoneNumber);
    ff.emailAddress = this.blankToNull(currentCompany.emailAddress);
    ff.webSiteUrl = this.blankToNull(currentCompany.websiteURL);
    ff.crNumber = currentCompany.crNumber;
    ff.latitude = currentCompany.location?.latitude ?? null;
    ff.longitude = currentCompany.location?.longitude ?? null;
    ff.companyId = currentCompany.id;
    ff.placeId = currentCompany.placeId;
    ff.logo = this.blankToNull(currentCompany.logo);
    ff.defaultContactId = this.resolveDefaultContactId(currentCompany);

    this.isRegisteringForwarder.set(true);
    this.freightForwarderService.addFreightForwarder(ff).pipe(
      switchMap(savedForwarder => {
        if (!savedForwarder?.id) {
          throw new Error('Freight forwarder registration failed.');
        }
        const updatedCompany = { ...currentCompany, freightForwarderId: savedForwarder.id };
        this.freightForwarder.set(savedForwarder);
        this.sessionStorage.saveFreightForwarder(savedForwarder);
        return this.companyService.updateCompany(updatedCompany);
      }),
      finalize(() => this.isRegisteringForwarder.set(false))
    ).subscribe({
      next: response => {
        if (!response.success) {
          this.messageService.add({ severity: 'error', summary: 'Company update failed', detail: response.message });
          return;
        }
        this.company.set(response.data);
        this.sessionStorage.saveCompany(response.data);
        this.messageService.add({ severity: 'success', summary: 'Freight forwarder registered', detail: 'Company is now registered as a freight forwarder.' });
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Registration failed',
        detail: error?.message || 'Unable to register freight forwarder.'
      })
    });
  }

  openInviteDialog(): void {
    const currentCompany = this.company();
    this.inviteForm.reset({
      name: '',
      emailAddress: '',
      phoneNumber: '',
      corporateRole: 'EMPLOYEE',
      address: currentCompany?.address ?? ''
    });
    this.inviteRoleIds.set(new Set<number>());
    this.inviteDialogVisible.set(true);
  }

  toggleInviteRole(roleId: number, checked: boolean): void {
    this.inviteRoleIds.update(ids => this.toggleRoleId(ids, roleId, checked));
  }

  inviteEmployee(): void {
    const currentCompany = this.company();
    if (!currentCompany?.id) {
      return;
    }

    if (this.inviteForm.invalid) {
      this.inviteForm.markAllAsTouched();
      return;
    }

    const value = this.inviteForm.getRawValue();
    this.isSaving.set(true);
    this.companyService.inviteEmployee(currentCompany.id, {
      name: value.name ?? '',
      emailAddress: value.emailAddress ?? '',
      phoneNumber: value.phoneNumber ?? '',
      corporateRole: value.corporateRole ?? 'EMPLOYEE',
      address: value.address,
      countryId: currentCompany.countryId,
      roleIds: Array.from(this.inviteRoleIds())
    }).pipe(
      finalize(() => this.isSaving.set(false))
    ).subscribe({
      next: response => {
        if (!response.success) {
          this.messageService.add({ severity: 'error', summary: 'Invite failed', detail: response.message });
          return;
        }
        this.messageService.add({ severity: 'success', summary: 'Employee invited', detail: response.message });
        this.inviteDialogVisible.set(false);
        this.loadCompanyContext();
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Invite failed',
        detail: error?.message || 'Unable to invite employee.'
      })
    });
  }

  openRoleDialog(customer: Customer): void {
    this.roleCustomer.set(customer);
    this.selectedRoleIds.set(new Set<number>((customer.roles ?? []).map(role => role.roleId)));
    this.roleDialogVisible.set(true);
  }

  toggleSelectedRole(roleId: number, checked: boolean): void {
    this.selectedRoleIds.update(ids => this.toggleRoleId(ids, roleId, checked));
  }

  saveRoles(): void {
    const customer = this.roleCustomer();
    if (!customer?.id) {
      return;
    }

    const currentIds = new Set<number>((customer.roles ?? []).map(role => role.roleId));
    const nextIds = this.selectedRoleIds();
    const additions = Array.from(nextIds).filter(roleId => !currentIds.has(roleId));
    const removals = Array.from(currentIds).filter(roleId => !nextIds.has(roleId));
    const operations = [
      ...additions.map(roleId => {
        const role = this.roles().find(item => item.roleId === roleId);
        return role ? this.customerService.addRoleToCustomer(customer.id, role) : of(null);
      }),
      ...removals.map(roleId => this.customerService.deleteRoleFromCustomer(customer.id, roleId))
    ];

    if (operations.length === 0) {
      this.roleDialogVisible.set(false);
      return;
    }

    this.isSaving.set(true);
    forkJoin(operations).pipe(
      finalize(() => this.isSaving.set(false))
    ).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Roles updated', detail: 'Employee roles were saved.' });
        this.roleDialogVisible.set(false);
        this.loadCompanyContext();
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Role update failed',
        detail: error?.message || 'Unable to save employee roles.'
      })
    });
  }

  openFileDialog(): void {
    this.selectedFileType.set('VAT_CERTIFICATE');
    this.selectedFile.set(null);
    this.fileDialogVisible.set(true);
  }

  onFileTypeChanged(value: string): void {
    this.selectedFileType.set(value as CompanyRegistrationAttachmentCategory);
  }

  onCompanyFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile.set(input.files?.[0] ?? null);
  }

  uploadCompanyFile(): void {
    const currentCompany = this.company();
    const file = this.selectedFile();
    if (!currentCompany?.id || !file) {
      this.messageService.add({ severity: 'warn', summary: 'File required', detail: 'Select a file to upload.' });
      return;
    }

    const objectName = `${Date.now()}_${file.name}`;
    this.isUploadingFile.set(true);
    this.storageService.uploadFile(`companies/${currentCompany.id}/registration-files/${objectName}`, file).pipe(
      switchMap(fileUrl => this.companyService.saveCompanyRegistrationAttachment(currentCompany.id, {
        ownerCompanyId: currentCompany.id,
        category: this.selectedFileType(),
        fileName: file.name,
        fileUrl,
        fileType: file.type,
        fileSize: file.size,
        uploadedByCustomerId: this.sessionStorage.getCustomer()?.id
      })),
      finalize(() => this.isUploadingFile.set(false))
    ).subscribe({
      next: response => {
        if (response.data) {
          this.companyFiles.update(files => [response.data, ...files]);
        }
        this.fileDialogVisible.set(false);
        this.messageService.add({ severity: 'success', summary: 'File uploaded', detail: 'Company file was saved.' });
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Upload failed',
        detail: error?.message || 'Unable to upload company file.'
      })
    });
  }

  deleteCompanyFile(file: CompanyRegistrationAttachment): void {
    const currentCompany = this.company();
    if (!currentCompany?.id || !file.id) {
      return;
    }
    this.companyService.deleteCompanyRegistrationAttachment(currentCompany.id, file.id).subscribe({
      next: () => {
        this.companyFiles.update(files => files.filter(item => item.id !== file.id));
        this.messageService.add({ severity: 'success', summary: 'File deleted', detail: 'Company file was removed.' });
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Delete failed',
        detail: error?.message || 'Unable to delete company file.'
      })
    });
  }

  fileTypeLabel(category: string): string {
    return this.companyFileTypes.find(type => type.value === category)?.label || category;
  }

  roleNames(customer: Customer): string[] {
    return (customer.roles ?? []).map(role => role.roleName).filter(Boolean);
  }

  formatBoolean(value: boolean | undefined): string {
    return value ? 'Yes' : 'No';
  }

  private resolveDefaultContactId(company: Company): number | null {
    return company.defaultContactId ?? this.sessionStorage.getCustomer()?.id ?? this.employees()[0]?.id ?? null;
  }

  private blankToNull(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }

  private toggleRoleId(source: Set<number>, roleId: number, checked: boolean): Set<number> {
    const next = new Set<number>(source);
    if (checked) {
      next.add(roleId);
    } else {
      next.delete(roleId);
    }
    return next;
  }
}
